// Modulation: a source routed to a parameter.
//
// The pipeline README has promised this since the beginning — "crossing all
// six: modulation (any source routed to any parameter)" — and this is the
// first source to exist. Pointer hover.
//
// What can be bound is decided by the tier, for the same reason the tween uses
// it: `structural` means a rebuild, so modulating one at 60fps would
// reallocate the substrate every frame. Continuous and cosmetic numbers are
// exactly the ones built to move, so those are the ones offered.
//
// The value moves within its own declared range — amount 1 sweeps the whole of
// it — so a binding cannot push a parameter somewhere the panel could not.

import { fields } from './fields/index.js'
import { substrates } from './substrates/index.js'
import { motionParams } from './motion/index.js'
import { markParams, inkParams, frameParams } from './marks.js'
import { clockParams } from './clock.js'

export const NONE = 'none'

export const pointerParams = [
  { key: 'amount', label: 'depth', tier: 'continuous', min: 0, max: 1, step: 0.02, default: 0.35,
    hint: 'how far through the range a hover reaches' },
  { key: 'jitter', label: 'randomness', tier: 'continuous', min: 0, max: 1, step: 0.02, default: 0.6,
    hint: '0 follows the pointer, 1 wanders on its own' },
]

// Sections addressed as "<section>.<key>". The first two live on the piece,
// the rest on every layer.
const TOP = { clock: clockParams, frame: frameParams }

function paramsFor(section, layer) {
  if (TOP[section]) return TOP[section]
  if (!layer) return null
  if (section === 'field') return (fields[layer.field.type] || fields.interference).params
  if (section === 'substrate') return (substrates[layer.substrate.type] || substrates.grid).params
  if (section === 'motion') return motionParams
  if (section === 'mark') return markParams
  if (section === 'ink') return inkParams
  return null
}

// A number that is allowed to move. Structural is excluded on purpose.
const movable = (p) => p.min !== undefined && p.tier !== 'structural'

// Built from whatever schemas the piece is currently using, so a binding list
// follows the field and substrate types the way the panel's controls do.
export function bindable(layer) {
  const out = [{ value: NONE, label: 'none' }]
  for (const section of ['field', 'substrate', 'motion', 'mark', 'ink', 'frame', 'clock']) {
    const params = paramsFor(section, layer)
    if (!params) continue
    for (const p of params) {
      if (movable(p)) out.push({ value: section + '.' + p.key, label: section + ' ' + p.label })
    }
  }
  return out
}

// signal is -1..1. Applied to every layer rather than the first: a binding
// names a parameter of the piece, and a six-layer chapter modulating only one
// of them would read as a glitch rather than as a response.
export function modulate(piece, signal) {
  const cfg = piece.pointer
  if (!cfg || cfg.path === NONE || !signal) return piece
  const dot = cfg.path.indexOf('.')
  if (dot < 0) return piece
  const section = cfg.path.slice(0, dot)
  const key = cfg.path.slice(dot + 1)

  const shift = (params, target) => {
    const p = params && params.find((q) => q.key === key)
    if (!p || !movable(p)) return target
    const span = (p.max - p.min) * cfg.amount * signal
    // Circular params wrap rather than clamp — a hue driven past 360 should
    // keep going round, not pile up at the end of the slider.
    if (p.wrap) {
      const h = (((target[key] + span) % p.wrap) + p.wrap) % p.wrap
      return { ...target, [key]: p.step >= 1 ? Math.round(h) % p.wrap : h }
    }
    const v = Math.min(p.max, Math.max(p.min, target[key] + span))
    return { ...target, [key]: p.step >= 1 ? Math.round(v) : v }
  }

  if (TOP[section]) return { ...piece, [section]: shift(TOP[section], piece[section]) }
  return {
    ...piece,
    layers: piece.layers.map((l) => ({ ...l, [section]: shift(paramsFor(section, l), l[section]) })),
  }
}

// Hover strength eased toward its target, times a wander. Three sines at
// irrational-ish ratios never repeat on any timescale anyone will watch for,
// which is enough randomness here and costs no state.
export function pointerSignal(hover, t, px, jitter) {
  if (hover <= 0.0005) return 0
  const wander = Math.sin(t * 1.7 + 0.3) * 0.55
    + Math.sin(t * 2.9 + 1.1) * 0.3
    + Math.sin(t * 4.3 + 2.7) * 0.15
  return hover * ((1 - jitter) * px + jitter * wander)
}
